import React from 'react';

import JSZip from 'jszip';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { useProgramLibraryStore } from 'store/programLibrary.store';

import { UploadXerPayload, useMutationUploadXerFile } from 'api/mutations/uploadXerFile.mutation';

import { UploadMppModal } from 'components/common/UploadFileModal/components/UploadMppModal/UploadMppModal';
import { UploadPlaniswereModal } from 'components/common/UploadFileModal/components/UploadPlaniswereModal/UploadPlaniswereModal';
import { UploadXerModal } from 'components/common/UploadFileModal/components/UploadXerModal/UploadXerModal';
import { FileExtensions, IUploadFiledFetch } from 'components/common/UploadFileModal/components/programLibrary.types';

export const RenderUploadFilesModal = () => {
    const modalShow = useProgramLibraryStore((state) => state.modalShow);
    const details = useProgramLibraryStore((state) => state.details);
    const isUploading = useProgramLibraryStore((state) => state.isUploading);
    const { update } = useProgramLibraryStore();
    const mutationUploadXerFile = useMutationUploadXerFile();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const queryClient = useQueryClient();
    const { username } = useUserHook();
    const { handleSnackBar } = useCustomSnackBar();

    const handleUploadFiledFetch = ({ details, contract, project }: IUploadFiledFetch) => {
        if (details.file && username) {
            update({ isUploading: true });
            const fileName = `${details.file.name}.zip`;
            const zip = new JSZip();
            zip.file(details.file.name, details.file);
            zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: {
                    level: 9,
                },
            }).then((blob) => {
                const versions = details.versions.filter((item) => item.download);

                const regularPayload: UploadXerPayload = {
                    projectId: project.id,
                    contractId: contract ? contract.id : null,
                    contractName: contract ? contract.title : 'All',
                    fileName,
                    uploadingUser: username,
                    file: blob,
                    versions,
                    isBaseline: [details.type === 'xer' ? details.isBaseline : details.versions[0].isBaseline],
                    versionId: [details.versions[0].versionId],
                    plf_file: details.plf,
                    anotherFile: details.relationsFile,
                    language: details.language ? details.language.value : null,
                    isTransformMPP: details.isTransformMPP,
                    customVersionName: details.customVersionName,
                    isIntegrated: details.isIntegrated,
                    runCPM: details.runCPM ?? false,
                };

                mutationUploadXerFile.mutate(regularPayload, {
                    onSuccess: () => {
                        handleSnackBar('File uploaded. Processing… ', 'success');
                        if (details.file) {
                            sendEventWithDimensions({
                                category: 'Program Library',
                                action: 'File upload',
                                label: details.file.name,
                            });
                        }
                    },
                    onError: (error) => {
                        let errorMessage = '';
                        switch (error.response.status) {
                            case 409:
                                errorMessage = error.response.data;
                                break;
                            case 403:
                                errorMessage = 'Unauthorized request, please contact support.';
                                break;
                            default:
                                errorMessage = 'Error uploading file, please contact support';
                        }
                        DEFAULT_ERROR({ error, message: errorMessage, handleError: handleSnackBar });
                    },
                    onSettled: () => {
                        update({ modalShow: false });
                        return queryClient.invalidateQueries('getProjectVersions').then(() => {
                            update({ isUploading: false });
                        });
                    },
                });
            });
        }
    };

    return (
        <>
            {details?.type === FileExtensions.xer && (
                <UploadXerModal
                    open={modalShow}
                    details={details}
                    isUploading={isUploading}
                    handleFetchData={handleUploadFiledFetch}
                    onClose={() => update({ modalShow: false })}
                />
            )}
            {details?.type === FileExtensions.mpp && (
                <UploadMppModal
                    open={modalShow}
                    details={details}
                    isUploading={isUploading}
                    handleFetchData={handleUploadFiledFetch}
                    onClose={() => update({ modalShow: false })}
                />
            )}
            {details?.type === FileExtensions.xlsx && (
                <UploadPlaniswereModal
                    open={modalShow}
                    details={details}
                    isUploading={isUploading}
                    handleFetchData={handleUploadFiledFetch}
                    onClose={() => update({ modalShow: false })}
                />
            )}
        </>
    );
};
